import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useOrg } from '@/context/OrgContext'
import type { Category, Produk, ProdukTipe } from '@/types'

const PAGE_SIZE = 8

async function normalizeProduk(
  data: any[],
  masterCache: Record<string, { nama_item: string; kategori_id: string }>,
  satuanCache: Record<string, string>,
): Promise<Produk[]> {
  if (data.length === 0) return []

  const masterIds = [...new Set(data.map(p => p.master_item_id).filter(Boolean))]
    .filter(id => !masterCache[id])
  const satuanIds = [...new Set(data.map(p => p.satuan_id).filter(Boolean))]
    .filter(id => !satuanCache[id])

  const [{ data: masters }, { data: satuans }] = await Promise.all([
    masterIds.length > 0
      ? supabase.from('master_item').select('id, nama_item, kategori_id').in('id', masterIds)
      : Promise.resolve({ data: [] }),
    satuanIds.length > 0
      ? supabase.from('satuan').select('id, nama').in('id', satuanIds)
      : Promise.resolve({ data: [] }),
  ])

  for (const m of masters ?? []) masterCache[m.id] = m
  for (const s of satuans ?? []) satuanCache[s.id] = s.nama

  return data.map(p => ({
    ...p,
    nama: masterCache[p.master_item_id]?.nama_item || '',
    satuan: satuanCache[p.satuan_id] || 'unit',
  }))
}

export function useProductsByCategory(slug: string | undefined, tipeFilter?: ProdukTipe) {
  const { orgId, loading: orgLoading } = useOrg()
  const [category, setCategory] = useState<Category | null>(null)
  const [products, setProducts] = useState<Produk[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [notFound, setNotFound] = useState(false)

  const pageRef = useRef(0)
  const catIdRef = useRef<string | null>(null)
  const masterItemIdsRef = useRef<string[] | null>(null)
  const masterCacheRef = useRef<Record<string, { nama_item: string; kategori_id: string }>>({})
  const satuanCacheRef = useRef<Record<string, string>>({})
  const isSemua = slug === 'semua'

  useEffect(() => {
    if (!slug || orgLoading) return

    pageRef.current = 0
    catIdRef.current = null
    masterItemIdsRef.current = null
    masterCacheRef.current = {}
    satuanCacheRef.current = {}
    setProducts([])
    setCategory(null)
    setLoading(true)
    setNotFound(false)

    async function init() {
      let catId: string | null = null

      if (!isSemua) {
        const { data: cat } = await supabase
          .from('categories')
          .select('*')
          .eq('slug', slug!)
          .single()

        if (!cat) {
          setNotFound(true)
          setLoading(false)
          return
        }

        setCategory(cat)
        catId = cat.id
      }

      catIdRef.current = catId

      let masterItemIds: string[] | null = null
      if (catId) {
        const { data: mi } = await supabase
          .from('master_item')
          .select('id')
          .eq('kategori_id', catId)
          .eq('status', 'approved')
        masterItemIds = (mi ?? []).map(m => m.id)
        masterItemIdsRef.current = masterItemIds
        if (masterItemIds.length === 0) {
          setProducts([])
          setHasMore(false)
          pageRef.current = 1
          setLoading(false)
          return
        }
      }

      let query = supabase
        .from('produk')
        .select('id, deskripsi, harga, harga_max, tipe, stok, sewa_per_hari, gambar_url, aktif, category_id, satuan_id, master_item_id, created_at')
        .eq('aktif', true)
        .order('created_at', { ascending: false })
        .range(0, PAGE_SIZE - 1)

      if (masterItemIds) query = query.in('master_item_id', masterItemIds)
      if (orgId) query = query.eq('organization_id', orgId)
      if (tipeFilter) query = query.eq('tipe', tipeFilter)

      const { data } = await query
      const normalized = await normalizeProduk(data ?? [], masterCacheRef.current, satuanCacheRef.current)
      setProducts(normalized)
      setHasMore((data?.length ?? 0) === PAGE_SIZE)
      pageRef.current = 1
      setLoading(false)
    }

    init()
  }, [slug, orgId, orgLoading, tipeFilter])

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)

    const from = pageRef.current * PAGE_SIZE
    const to = from + PAGE_SIZE - 1

    const masterItemIds = masterItemIdsRef.current

    let query = supabase
      .from('produk')
      .select('id, deskripsi, harga, tipe, stok, gambar_url, aktif, category_id, satuan_id, master_item_id, created_at')
      .eq('aktif', true)
      .order('created_at', { ascending: false })
      .range(from, to)

    if (masterItemIds) query = query.in('master_item_id', masterItemIds)
    if (orgId) query = query.eq('organization_id', orgId)
    if (tipeFilter) query = query.eq('tipe', tipeFilter)

    const { data } = await query
    const normalized = await normalizeProduk(data ?? [], masterCacheRef.current, satuanCacheRef.current)
    setProducts(prev => [...prev, ...normalized])
    setHasMore((data?.length ?? 0) === PAGE_SIZE)
    pageRef.current++
    setLoadingMore(false)
  }, [hasMore, loadingMore, orgId, tipeFilter])

  return { category, products, loading, loadingMore, hasMore, loadMore, notFound }
}
